import axios, { AxiosError, type AxiosInstance } from 'axios';
import { signinRedirect } from '../apis/auth.api';
import { getHttpStatusCodeName, HttpStatusCode } from '../constants/httpStatusCode.enum';
import { getAccessTokenFromLS, clearLS } from './auth';
import { showNotification } from './notification';

const extractErrorMessage = (data: unknown): string => {
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (data instanceof Blob) return '';
  if (typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const message = record.message ?? record.error ?? record.detail ?? record.title;

    if (typeof message === 'string') return message;
    if (Array.isArray(message)) {
      return message.filter((item): item is string => typeof item === 'string').join(', ');
    }

    try {
      return JSON.stringify(data);
    } catch {
      return 'Đã xảy ra lỗi không xác định.';
    }
  }

  return String(data);
};

class Http {
  instance: AxiosInstance;

  constructor(version = 'v1', baseUrl?: string, extraHeaders?: Record<string, string>) {
    const defaultBaseURL =
      process.env.NODE_ENV === 'production'
        ? `${process.env.REACT_APP_API_ENDPOINT_PROD}/${version}`
        : `${process.env.REACT_APP_API_ENDPOINT}/${version}`;

    const finalBaseURL = baseUrl ? `${baseUrl.replace(/\/$/, '')}/${version}` : defaultBaseURL;

    this.instance = axios.create({
      baseURL: finalBaseURL,
      timeout: 60000 * 5,
      headers: {
        'Content-Type': 'application/json',
        ...extraHeaders
      }
    });

    this.instance.interceptors.request.use(
      async (config) => {
        const accessToken = getAccessTokenFromLS();
        if (config.headers) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === HttpStatusCode.Unauthorized) {
          clearLS();
          localStorage.setItem('returnUrl', window.location.pathname);

          if (!window.location.pathname.includes('/signin-oidc')) {
            await signinRedirect();
          }
        } else {
          if (error.response && error.response.data instanceof Blob) {
            const text = await error.response.data.text();
            showNotification(text, 'error');
            console.error('Server error:', text);
          } else if (error.response?.status) {
            const errMessage =
              extractErrorMessage(error?.response?.data) || getHttpStatusCodeName(error.response.status);
            showNotification(errMessage, 'error');
            console.error('Server error:', errMessage);
          }
        }
        return Promise.reject(error);
      }
    );
  }
}

const http = new Http().instance;
const httpV2 = new Http('v2').instance;

const httpExternal = new Http('v1', 'https://pay-apigw.martech.com.vn', {
  'X-Integration-Secret': process.env.REACT_APP_PAYROLL_INTEGRATION_SECRET || ''
}).instance;

export { http, httpV2, httpExternal };
